import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';

/*
 * The one way to ask "are you sure?".
 *
 * Replaces window.confirm, which blocks the whole tab, cannot be styled, cannot
 * be tested without stubbing a global, and — because Chrome lets a user suppress
 * further dialogs from a page — can silently start returning false for the rest
 * of the session.
 *
 * Deliberately unopinionated about what happens after `onConfirm`: every caller
 * already has its own in-flight and error surface next to the control that
 * opened this, so the dialog closes and gets out of the way rather than growing
 * a second place for the same error to appear.
 */
export type ConfirmDialogProps = {
  open: boolean;
  title: ReactNode;
  /** The consequence, in a sentence. This is what the user actually reads. */
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for anything destructive or irreversible. */
  tone?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
};

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          {/* Dismiss first in DOM order: Modal focuses its close button on open
              and Tab walks forward from there, so the destructive control is
              never the thing a keyboard user reaches by reflex. */}
          <Button onClick={onCancel}>{cancelLabel}</Button>
          <Button variant={tone} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}
